/**
 * The route table: names and paths only.
 *
 * Kept separate from routeConfiguration.js, which binds these to page
 * components, because link helpers need to turn a route *name* into a path
 * without pulling in a single page.
 *
 * Importing the full configuration from those helpers created a cycle —
 * routeConfiguration -> a page -> components -> NamedLink ->
 * routeConfiguration. `React.lazy` hid it by deferring the page imports; the
 * moment the pages became static imports the cycle became real and the
 * bundle threw "Cannot access 'X' before initialization" at load. This file
 * imports only pure data modules, which import nothing themselves, so it
 * still cannot take part in a cycle.
 *
 * Add a route here and in routeConfiguration.js together — except service
 * pages, which are generated from config and need neither file touched.
 */
import {
  configServicePages,
  allServicePageIds,
  serviceRouteName,
} from './config/configServicePages';

/**
 * One route per service page, derived from config rather than listed by hand.
 *
 * Driven by configServicePages, not configServices: a page does not have to
 * have a card in the landing grid. QA, product management and maintenance are
 * pages without one. Adding an entry there produces its route, its
 * prerendered HTML and its sitemap line with no further edits.
 *
 * `serviceId` reaches ServicePage through `extraProps` in
 * routeConfiguration.js, so all of these routes share one component with no
 * path-parameter machinery — which is what let NamedLink and util/routes.js
 * stay parameter-free.
 */
const serviceRoutes = allServicePageIds.map(id => ({
  path: `/services/${configServicePages[id].slug}`,
  name: serviceRouteName(id),
  exact: true,
  serviceId: id,
}));

const routePaths = [
  { path: '/', name: 'LandingPage', exact: true },
  /*
   * The hub. It is the parent every service page's breadcrumb points at, the
   * target of the topbar's Services link, and what lets the footer carry one
   * "All services" link instead of eleven.
   */
  { path: '/services', name: 'ServicesPage', exact: true },
  ...serviceRoutes,
  { path: '/privacy-policy', name: 'PrivacyPolicyPage', exact: true },
  { path: '/terms-of-service', name: 'TermsOfServicePage', exact: true },
  { path: '/notfound', name: 'NotFoundPage', exact: true },
];

export default routePaths;
